import { Check, Plus } from "lucide-react";
import { Icon } from "@cntrl/ui";
import type { GuestPartnerOption, GuestPartnerOptions } from "../../app/hooks/useGuestList.js";
import { partnerRelationshipLabel } from "../../app/partnerRelationship.js";
import styles from "./PartnerChips.module.scss";

export interface PartnerChipsProps {
  options: GuestPartnerOptions | undefined;
  /** The link currently chosen (its name is filled into the form and locked), or null. */
  selected: string | null;
  onSelect: (option: GuestPartnerOption | null) => void;
}

/**
 * Comp a PARTNER from the Add Guest form (ART-239 part 3) — one shape on the door sheet and the desktop modal.
 *   * A band member (`scope: "own"`) sees ONE chip, "+ Bring partner" (product owner: it replaces a picker).
 *   * An AV / Tour Manager (`scope: "all"`) sees one chip per member's partner, named, so every partner can be
 *     comped from one list.
 * A tap fills the form's name with the name the partner goes on the list as and locks it; tapping again lets go. A partner
 * already on this show's list is dimmed and reads "On list" — the server refuses a second row anyway. Nothing
 * renders when there is no one to offer (no partner, a pending invite, a seat with no band seat).
 */
export function PartnerChips({ options, selected, onSelect }: PartnerChipsProps) {
  const items = options?.items ?? [];
  if (items.length === 0) return null;
  const own = options?.scope === "own";

  return (
    <div className={styles.row} role="group" aria-label={own ? `Bring your ${partnerRelationshipLabel(items[0]?.relationship)?.toLowerCase() ?? "partner"}` : "Partners"}>
      {!own && <span className={styles.label}>Partners</span>}
      {items.map((o) => {
        const on = selected === o.partnerLinkId;
        const kind = partnerRelationshipLabel(o.relationship)?.toLowerCase() ?? "partner";
        const whose = `${o.memberFirstName ?? o.memberName}'s ${kind}`;
        const text = own ? (o.onList ? "On list" : `Bring ${kind}`) : o.guestName;
        return (
          <button
            key={o.partnerLinkId}
            type="button"
            className={styles.chip}
            aria-pressed={on}
            disabled={o.onList}
            data-on-list={o.onList || undefined}
            title={own ? (o.guestName ?? undefined) : whose}
            onClick={() => onSelect(on ? null : o)}
          >
            <Icon icon={on || o.onList ? Check : Plus} size={16} />
            <span>{text}</span>
            {!own && (
              <span className={styles.meta}>
                {o.onList ? "On list" : whose}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
